/**
 * Portfolio Service für die Berechnung von Portfolio-Positionen aus Transaktionsdaten
 * Implementiert FIFO-basierte Positionsberechnung
 */

import {
  TransaktionsDaten,
  PortfolioPosition,
  PurchaseLot,
  SteuerParameter,
  type OptimizationLotToSell,
  type OptimizationSellInstruction
} from '@/types';

/** Wenn totalValue und Menge×Kurs stark abweichen, Menge×Kurs für konsistente FIFO-Anschaffungskosten nutzen */
const BUY_TOTAL_VALUE_TOLERANCE = 0.03;
import { TAX_RATES, calculateEffectiveCapitalGainsTax } from '@/utils/constants';
import { berechneNettoKapitalsteuerNachVerrechnung, portfolioPositionKey } from '@/utils/capitalGainsAggregation';
import { validateCapitalGainsTax, validateEffectiveTaxRate, devValidate } from '@/utils/taxValidation';

export class PortfolioService {
  /**
   * Plausibilisierung Kauf/Transfer: bei widersprüchlichem totalValue zu quantity×price den rechnerischen Wert nutzen.
   */
  private static resolveBuyTotalValue(transaktion: TransaktionsDaten): number {
    const { quantity, price, totalValue } = transaktion;
    const fromProduct = quantity * price;
    if (fromProduct <= 0) {
      return totalValue > 0 ? totalValue : 0;
    }
    const relDiff = Math.abs(totalValue - fromProduct) / fromProduct;
    if (relDiff > BUY_TOTAL_VALUE_TOLERANCE) {
      return fromProduct;
    }
    return totalValue;
  }

  /**
   * Steuer je Euro Verkaufserlös für den FIFO-Anfangstranche (ca. 1 Stück bzw. min. 0,01 % der Position),
   * statt Quote aus Vollverkauf — bessere Priorisierung bei heterogenen Lots.
   */
  private static marginalFifoTaxPerEuroOfProceeds(
    position: PortfolioPosition,
    steuerParameter: SteuerParameter | null
  ): number {
    if (position.totalValue <= 0 || !position.purchaseLots?.length) {
      return 0;
    }
    const qty = Math.max(position.quantity, 1e-12);
    const probePct = Math.min(100, Math.max(0.01, (1 / qty) * 100));
    const fifoTaxableProfit = this.calculateFIFOTaxableProfit(position, probePct);
    const profit = typeof fifoTaxableProfit === 'number' ? fifoTaxableProfit : 0;
    const tax = calculateEffectiveCapitalGainsTax(profit, steuerParameter?.kirchensteuer).gesamtsteuer;
    const saleValue = position.totalValue * (probePct / 100);
    return saleValue > 0 ? tax / saleValue : 0;
  }

  /**
   * Berechnet Portfolio-Positionen aus Transaktionsdaten
   * @param transaktionen - Array von Transaktionen
   * @param currentPricesByAsset - Optionale aktuelle Kurse pro Asset (z.B. von Parqet)
   * @param soldAssetIdentifiers - Optionale Liste von Asset-Identifiers, die als verkauft gelten (Parqet isSold) und ausgeschlossen werden
   * @param logosByAsset - Optionale Logo-URLs pro Asset (von Parqet API)
   * @returns Array von Portfolio-Positionen
   */
  static calculatePortfolioPositions(
    transaktionen: TransaktionsDaten[],
    currentPricesByAsset?: Record<string, number>,
    soldAssetIdentifiers?: string[],
    logosByAsset?: Record<string, string>
  ): PortfolioPosition[] {
    // Gruppiere Transaktionen nach ISIN + Portfolio (FIFO ist portfolio-spezifisch)
    const transaktionenNachISINUndPortfolio = this.groupTransactionsByISINAndPortfolio(transaktionen);
    const soldSet = soldAssetIdentifiers ? new Set(soldAssetIdentifiers) : undefined;

    const portfolioPositionen: PortfolioPosition[] = [];

    // Berechne Position für jede ISIN+Portfolio Kombination
    for (const [key, kombinationTransaktionen] of transaktionenNachISINUndPortfolio.entries()) {
      const [isin, portfolio] = key.split('|');
      // Parqet isSold: Asset aus Portfolio-Positionen ausschließen (auch wenn anteile > 0)
      if (soldSet?.has(isin)) {
        continue;
      }
      const position = this.calculatePositionForISINAndPortfolio(isin, portfolio, kombinationTransaktionen, currentPricesByAsset, logosByAsset);
      
      // Nur Positionen mit Bestand hinzufügen
      if (position && position.quantity > 0) {
        portfolioPositionen.push(position);
      }
    }

    return portfolioPositionen.sort((a, b) => b.totalValue - a.totalValue);
  }

  /**
   * Gruppiert Transaktionen nach ISIN + Portfolio (FIFO ist portfolio-spezifisch)
   */
  private static groupTransactionsByISINAndPortfolio(transaktionen: TransaktionsDaten[]): Map<string, TransaktionsDaten[]> {
    const gruppiert = new Map<string, TransaktionsDaten[]>();

    transaktionen.forEach(transaktion => {
      if (!transaktion.identifier) return;

      // Erstelle eindeutigen Schlüssel: ISIN|Portfolio
      const portfolio = transaktion.portfolio || 'Unknown';
      const key = `${transaktion.identifier}|${portfolio}`;

      if (!gruppiert.has(key)) {
        gruppiert.set(key, []);
      }
      gruppiert.get(key)!.push(transaktion);
    });

    // Sortiere Transaktionen nach Datum (FIFO innerhalb jedes Portfolios)
    gruppiert.forEach(transaktionsliste => {
      transaktionsliste.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    });

    return gruppiert;
  }

  /**
   * Berechnet Position für eine ISIN + Portfolio Kombination (portfolio-spezifisches FIFO)
   */
  private static calculatePositionForISINAndPortfolio(
    isin: string,
    portfolio: string,
    transaktionen: TransaktionsDaten[],
    currentPricesByAsset?: Record<string, number>,
    logosByAsset?: Record<string, string>
  ): PortfolioPosition | null {
    const kaufQueue: PurchaseLot[] = []; // FIFO-Queue für dieses spezifische Portfolio
    let gesamtQuantity = 0;
    let gesamtKaufwert = 0;
    let name = '';
    let waehrung = 'EUR';
    let letzterPreis = 0;

    transaktionen.forEach(transaktion => {
      // Aktualisiere Metadaten
      if (transaktion.name && !name) name = transaktion.name;
      if (transaktion.currency) waehrung = transaktion.currency;
      // Only use prices from buy/sell transactions, not dividends or other types
      if ((transaktion.type === 'buy' || transaktion.type === 'sell' || transaktion.type === 'transfer_in' || transaktion.type === 'transfer_out') && transaktion.price > 0) {
        letzterPreis = transaktion.price;
      }

      if (transaktion.type === 'buy' || transaktion.type === 'transfer_in') {
        // Kauf / Transfer ein: Zur FIFO-Queue hinzufügen (mit Portfolio-Info)
        const buyTotal = this.resolveBuyTotalValue(transaktion);
        kaufQueue.push({
          quantity: transaktion.quantity,
          price: transaktion.price,
          date: transaktion.date,
          portfolio: portfolio // Include portfolio info in lot (keep 'Unknown' as is)
        });
        gesamtQuantity += transaktion.quantity;
        gesamtKaufwert += buyTotal;
      } else if (transaktion.type === 'sell' || transaktion.type === 'transfer_out') {
        // Verkauf / Transfer aus: FIFO-basiert aus der Queue entfernen
        let zuVerkaufendeQuantity = transaktion.quantity;
        while (zuVerkaufendeQuantity > 0 && kaufQueue.length > 0) {
          const aeltesterKauf = kaufQueue[0];
          if (aeltesterKauf.quantity <= zuVerkaufendeQuantity) {
            // Gesamten Kauf verkaufen
            zuVerkaufendeQuantity -= aeltesterKauf.quantity;
            gesamtQuantity -= aeltesterKauf.quantity;
            gesamtKaufwert -= aeltesterKauf.quantity * aeltesterKauf.price;
            kaufQueue.shift();
          } else {
            // Teilverkauf
            aeltesterKauf.quantity -= zuVerkaufendeQuantity;
            gesamtQuantity -= zuVerkaufendeQuantity;
            gesamtKaufwert -= zuVerkaufendeQuantity * aeltesterKauf.price;
            zuVerkaufendeQuantity = 0;
          }
        }
      }
    });

    // Prüfe ob noch Bestände vorhanden
    if (gesamtQuantity <= 0) {
      return null;
    }

    // Berechne durchschnittlichen Kaufpreis
    const durchschnittlicherKaufpreis = gesamtKaufwert / gesamtQuantity;

    // Aktueller Preis: Parqet-Kurs falls vorhanden, sonst letzter Transaktionspreis
    const aktuellerPreis = currentPricesByAsset?.[isin] ?? letzterPreis;
    const gesamtwert = gesamtQuantity * aktuellerPreis;

    return {
      identifier: isin,
      name: name || isin,
      quantity: gesamtQuantity,
      averageBuyPrice: durchschnittlicherKaufpreis,
      currentPrice: aktuellerPreis,
      totalValue: gesamtwert,
      currency: waehrung,
      portfolio: portfolio,
      sellPercentage: 0,
      purchaseLots: kaufQueue.slice(), // Kopie der portfolio-spezifischen FIFO-Queue
      logo: logosByAsset?.[isin]
    };
  }

  /**
   * Berechnet FIFO-basierten steuerpflichtigen Gewinn für einen Verkauf
   * @param position - Portfolio-Position mit Kauf-Lots
   * @param sellPercentage - Verkaufs-Prozentsatz
   * @returns Steuerpflichtiger Gewinn basierend auf FIFO-Prinzip
   */
  static calculateFIFOTaxableProfit(position: PortfolioPosition, sellPercentage: number): number {
    const result = this.calculateFIFOTaxableProfitWithDetails(position, sellPercentage);
    
    // Berechne steuerpflichtigen Gewinn für jedes Lot einzeln
    let totalTaxableProfit = 0;
    result.soldLots.forEach(lot => {
      const taxableProfit = this.applyTaxTreatment(lot.profit, lot.effectiveTaxTreatment);
      totalTaxableProfit += taxableProfit;
    });
    
    return totalTaxableProfit;
  }

  /**
   * Wendet Steuerbehandlung (Teilfreistellungen) auf den Gewinn bzw. Verlust an.
   * Teilfreistellung nach InvStG wirkt symmetrisch: nur der steuerlich relevante Anteil
   * zählt (z. B. 70% bei 30% TF → Verlust −1.000 € wird zu −700 €).
   */
  static applyTaxTreatment(profit: number, taxTreatment?: string): number {
    if (!taxTreatment) {
      return profit;
    }

    switch (taxTreatment) {
      case 'Teilfreistellung 15%':
        return profit * 0.85;
      case 'Teilfreistellung 30%':
        return profit * 0.70;
      case 'Steuerfrei Haltefrist >1 Jahr':
        // Nach Ablauf der Spekulationsfrist (z. B. § 23 EStG): weder Gewinn noch Verlust einkommensteuerlich relevant
        return 0;
      case 'keine Teilfreistellung':
      default:
        return profit;
    }
  }

  /**
   * Berechnet FIFO-basierten steuerpflichtigen Gewinn mit detaillierten Lot-Informationen
   * @param position - Portfolio-Position mit Kauf-Lots
   * @param sellPercentage - Verkaufs-Prozentsatz
   * @returns Detaillierte FIFO-Berechnung mit Lot-Informationen
   */
  static calculateFIFOTaxableProfitWithDetails(position: PortfolioPosition, sellPercentage: number): {
    totalProfit: number;
    soldLots: Array<{
      quantity: number;
      purchasePrice: number;
      salePrice: number;
      profit: number;
      date: string;
      isTaxFree: boolean;
      effectiveTaxTreatment: string;
    }>;
  } {
    if (sellPercentage <= 0 || !position.purchaseLots || position.purchaseLots.length === 0) {
      return { totalProfit: 0, soldLots: [] };
    }

    const sellQuantity = position.quantity * (sellPercentage / 100);
    let remainingSellQuantity = sellQuantity;
    let totalProfit = 0;
    const soldLots: Array<{
      quantity: number;
      purchasePrice: number;
      salePrice: number;
      profit: number;
      date: string;
      isTaxFree: boolean;
      effectiveTaxTreatment: string;
    }> = [];
    
    // Kopie der Lots für FIFO-Simulation — immer nach Kaufdatum (älteste zuerst),
    // analog zu calculateFIFOLotsToSell (Reihenfolge im Array allein reicht nicht).
    const lots = [...position.purchaseLots].sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA.getTime() - dateB.getTime();
    });
    const currentDate = new Date();
    
    for (const lot of lots) {
      if (remainingSellQuantity <= 0) break;
      
      const quantityToSell = Math.min(remainingSellQuantity, lot.quantity);
      const costBasis = quantityToSell * lot.price;
      const saleValue = quantityToSell * position.currentPrice;
      const profit = saleValue - costBasis;
      
      // Prüfe Haltefrist für steuerfreie Behandlung
      const purchaseDate = new Date(lot.date);
      const holdingPeriodYears = (currentDate.getTime() - purchaseDate.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
      const isTaxFree = position.taxTreatment === 'Steuerfrei Haltefrist >1 Jahr' && holdingPeriodYears >= 1;
      
      // Bestimme effektive Steuerbehandlung für dieses Lot
      let effectiveTaxTreatment = position.taxTreatment || 'keine Teilfreistellung';
      if (position.taxTreatment === 'Steuerfrei Haltefrist >1 Jahr' && holdingPeriodYears < 1) {
        effectiveTaxTreatment = 'keine Teilfreistellung'; // Haltefrist nicht erfüllt
      }
      
      soldLots.push({
        quantity: quantityToSell,
        purchasePrice: lot.price,
        salePrice: position.currentPrice,
        profit: profit,
        date: lot.date,
        isTaxFree: isTaxFree,
        effectiveTaxTreatment: effectiveTaxTreatment
      });
      
      totalProfit += profit;
      remainingSellQuantity -= quantityToSell;
    }
    
    return { totalProfit, soldLots };
  }

  /**
   * Anschaffungskosten der nach einem FIFO-Teilverkauf verbleibenden Anteile (älteste Lots zuerst).
   */
  static calculateRemainingBuyingValueAfterPartialSale(
    position: PortfolioPosition,
    sellPercentage: number
  ): number {
    if (!position.purchaseLots || position.purchaseLots.length === 0) {
      return 0;
    }
    if (sellPercentage <= 0) {
      return position.purchaseLots.reduce((sum, lot) => sum + lot.quantity * lot.price, 0);
    }
    if (sellPercentage >= 100) {
      return 0;
    }

    const sellQuantity = position.quantity * (sellPercentage / 100);
    let remainingSellQty = sellQuantity;
    const sortedLots = [...position.purchaseLots].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    let remainingCost = 0;
    for (const lot of sortedLots) {
      if (remainingSellQty <= 0) {
        remainingCost += lot.quantity * lot.price;
        continue;
      }
      const soldFromLot = Math.min(lot.quantity, remainingSellQty);
      const qtyLeftInLot = lot.quantity - soldFromLot;
      remainingCost += qtyLeftInLot * lot.price;
      remainingSellQty -= soldFromLot;
    }
    return remainingCost;
  }

  /**
   * Aktualisiert Portfolio-Positionen mit neuen Kursen
   * @param positionen - Bestehende Positionen
   * @param kursDaten - Neue Kursdaten (ISIN -> Preis)
   */
  static updatePositionsWithCurrentPrices(
    positionen: PortfolioPosition[], 
    kursDaten: Map<string, number>
  ): PortfolioPosition[] {
    return positionen.map(position => {
      const neuerPreis = kursDaten.get(position.identifier);
      
      if (neuerPreis && neuerPreis > 0) {
        return {
          ...position,
          currentPrice: neuerPreis,
          totalValue: position.quantity * neuerPreis,
          purchaseLots: position.purchaseLots || [] // Preserve purchase lots
        };
      }
      
      return position;
    });
  }

  /**
   * Berechnet FIFO-basierte Steuerinformationen für einen Verkauf
   * @param position - Portfolio-Position
   * @param verkaufsProzent - Verkaufs-Prozentsatz
   * @param steuerParameter - Berechnungsparameter aus Schritt 1
   */
  static calculateFIFOTaxForSale(
    position: PortfolioPosition,
    verkaufsProzent: number,
    steuerParameter: SteuerParameter | null
  ) {
    const verkaufsQuantity = position.quantity * (verkaufsProzent / 100);
    const verkaufsWert = verkaufsQuantity * position.currentPrice;
    
    // Berechne Brutto-Gewinn (vor Teilfreistellung) und lot-by-lot Steuerbehandlung
    const fifoDetails = this.calculateFIFOTaxableProfitWithDetails(position, verkaufsProzent);
    const bruttoGewinn = fifoDetails.totalProfit;
    
    // Berechne steuerpflichtigen Gewinn lot-by-lot mit korrekter Haltefrist-Prüfung
    const steuerpflichtigerGewinn = this.calculateFIFOTaxableProfit(position, verkaufsProzent);

    // Use correct capital gains tax calculation with church tax deduction effect
    const taxCalculation = calculateEffectiveCapitalGainsTax(steuerpflichtigerGewinn, steuerParameter?.kirchensteuer);
    
    const kapitalertragsteuer = taxCalculation.kapitalertragsteuer;
    const solidaritaetszuschlag = taxCalculation.solidaritaetszuschlag;
    const kirchensteuer = taxCalculation.kirchensteuer;
    const gesamtsteuer = taxCalculation.gesamtsteuer;
    const nettoErloes = verkaufsWert - gesamtsteuer;

    const result = {
      verkaufsQuantity,
      verkaufsWert,
      gewinn: steuerpflichtigerGewinn, // Steuerpflichtiger Gewinn (nach lot-by-lot Behandlung)
      bruttoGewinn, // Brutto-Gewinn (vor Teilfreistellung)
      kapitalertragsteuer,
      solidaritaetszuschlag,
      kirchensteuer,
      gesamtsteuer,
      nettoErloes
    };

    // Validate calculation in development mode
    devValidate(validateCapitalGainsTax, steuerpflichtigerGewinn, steuerParameter, {
      kapitalertragsteuer,
      solidaritaetszuschlag,
      kirchensteuer,
      gesamtsteuer,
      steuerpflichtigerGewinn
    });

    return result;
  }

  /**
   * Berechnet die effektive Steuerrate basierend auf Berechnungsparametern
   * @param steuerParameter - Berechnungsparameter aus Schritt 1
   * @returns Effektive Steuerrate als Prozentsatz
   */
  static getEffectiveTaxRate(steuerParameter: SteuerParameter | null): number {
    // Use correct combined rates with church tax deduction effect
    let rate: number = TAX_RATES.BASE_CAPITAL_GAINS_RATE; // Base: 26.375% (25% KapESt + 1.375% Soli)
    
    if (steuerParameter?.kirchensteuer === 'baden-wuerttemberg-bayern') {
      rate = TAX_RATES.COMBINED_RATE_BW_BAYERN; // ~27.82% (with 8% church tax deduction effect)
    } else if (steuerParameter?.kirchensteuer === 'andere-bundeslaender') {
      rate = TAX_RATES.COMBINED_RATE_ANDERE; // ~27.99% (with 9% church tax deduction effect)
    }
    
    // Validate calculation in development mode
    devValidate(validateEffectiveTaxRate, steuerParameter, rate);
    
    return rate;
  }

  /**
   * FIFO-konforme Steuer-„Optimierung“ auf Positionsebene (keine freie Lot-Wahl).
   * Verkaufsreihenfolge zwischen Positionen nach Priorität; innerhalb einer Position striktes FIFO.
   */
  static calculateFIFOTaxOptimization(
    positions: PortfolioPosition[],
    targetAmount: number,
    steuerParameter: SteuerParameter | null,
    sparerPauschbetragEnabled: boolean = false,
    sparerPauschbetragBetrag: number = 0
  ): {
    optimizedPositions: PortfolioPosition[];
    totalProceeds: number;
    totalTaxAmount: number;
    totalTaxableProfit: number;
    totalFifoTaxableProfitBeforeAllowance: number;
    totalSteuerpflichtigerGewinnNachPausch: number;
    taxEfficiency: number;
    usedSparerPauschbetrag: number;
    sellInstructions: OptimizationSellInstruction[];
    strategy: string;
  } {
    return this.calculateFIFOCompliantOptimization(
      positions,
      targetAmount,
      steuerParameter,
      sparerPauschbetragEnabled,
      sparerPauschbetragBetrag
    );
  }

  /**
   * @deprecated Alter Name — suggerierte Lot-Level-Optimierung. Nutze {@link calculateFIFOTaxOptimization}.
   */
  static calculateOptimalLotLevelSales(
    positions: PortfolioPosition[],
    targetAmount: number,
    steuerParameter: SteuerParameter | null,
    sparerPauschbetragEnabled: boolean = false,
    sparerPauschbetragBetrag: number = 0
  ): ReturnType<typeof PortfolioService.calculateFIFOTaxOptimization> {
    return this.calculateFIFOTaxOptimization(
      positions,
      targetAmount,
      steuerParameter,
      sparerPauschbetragEnabled,
      sparerPauschbetragBetrag
    );
  }

  /**
   * FIFO-compliant tax optimization that respects the legal requirement to sell oldest lots first
   */
  /**
   * Calculate which specific lots to sell using FIFO rules
   */
  private static calculateFIFOLotsToSell(
    position: PortfolioPosition,
    sellPercentage: number
  ): OptimizationLotToSell[] {
    if (!position.purchaseLots || position.purchaseLots.length === 0) {
      return [];
    }
    
    // Create lots with original indices, then sort by purchase date (FIFO - oldest first)
    const lotsWithIndex = position.purchaseLots.map((lot, originalIndex) => ({
      ...lot,
      originalIndex
    })).sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA.getTime() - dateB.getTime();
    });
    
    const totalQuantity = position.quantity;
    const quantityToSell = totalQuantity * (sellPercentage / 100);
    
    const lotsToSell: OptimizationLotToSell[] = [];
    let remainingQuantityToSell = quantityToSell;
    
    for (let i = 0; i < lotsWithIndex.length && remainingQuantityToSell > 0; i++) {
      const lot = lotsWithIndex[i];
      const lotQuantity = lot.quantity;
      
      if (remainingQuantityToSell >= lotQuantity) {
        // Sell entire lot
        lotsToSell.push({
          lotIndex: lot.originalIndex,
          quantity: lotQuantity,
          partialSale: false
        });
        remainingQuantityToSell -= lotQuantity;
      } else {
        // Sell partial lot
        lotsToSell.push({
          lotIndex: lot.originalIndex,
          quantity: remainingQuantityToSell,
          partialSale: true
        });
        remainingQuantityToSell = 0;
      }
    }
    
    return lotsToSell;
  }

  static calculateFIFOCompliantOptimization(
    positions: PortfolioPosition[],
    targetAmount: number,
    steuerParameter: SteuerParameter | null,
    sparerPauschbetragEnabled: boolean = false,
    sparerPauschbetragBetrag: number = 0
  ): {
    optimizedPositions: PortfolioPosition[];
    totalProceeds: number;
    totalTaxAmount: number;
    totalTaxableProfit: number;
    totalFifoTaxableProfitBeforeAllowance: number;
    totalSteuerpflichtigerGewinnNachPausch: number;
    taxEfficiency: number;
    usedSparerPauschbetrag: number;
    sellInstructions: OptimizationSellInstruction[];
    strategy: string;
  } {
    // Step 1: Analyze what FIFO will actually sell for each position
    interface PositionFIFOAnalysis {
      position: PortfolioPosition;
      positionIndex: number;
      maxValue: number;
      fifoTaxableProfit: number;
      taxPerEuro: number;
      hasLosses: boolean;
      hasTaxFreeGains: boolean;
      priority: number;
    }
    
    const positionAnalysis: PositionFIFOAnalysis[] = [];
    
    positions.forEach((position, positionIndex) => {
      if (position.exclude || !position.purchaseLots || position.totalValue <= 0) return;
      
      // Calculate what FIFO would sell if we sell 100% of this position
      const fifoResult = this.calculateFIFOTaxableProfit(position, 100);
      const fifoTaxableProfit = typeof fifoResult === 'number' ? fifoResult : 0;

      const taxPerEuro = this.marginalFifoTaxPerEuroOfProceeds(position, steuerParameter);
      
      // Analyze lot characteristics
      let hasLosses = false;
      let hasTaxFreeGains = false;
      
      position.purchaseLots.forEach((lot) => {
        const currentPrice = position.currentPrice || 0;
        const currentValue = lot.quantity * currentPrice;
        const totalCost = lot.quantity * lot.price;
        const profit = currentValue - totalCost;
        
        // Check holding period for tax-free status
        let holdingPeriodYears = 0;
        try {
          const purchaseDate = new Date(lot.date);
          if (!isNaN(purchaseDate.getTime())) {
            holdingPeriodYears = (Date.now() - purchaseDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
          }
        } catch {
          // Ignore date parsing errors
        }
        
        const isTaxFree = (position.taxTreatment === 'Steuerfrei Haltefrist >1 Jahr' && holdingPeriodYears >= 1);
        
        if (profit < 0) hasLosses = true;
        if (isTaxFree && profit > 0) hasTaxFreeGains = true;
      });
      
      // Priorität ohne sequentiellen Pauschbetrag (Verrechnung erfolgt gesamt nach Auswahl)
      let priority: number;
      if (fifoTaxableProfit < 0) {
        priority = Math.abs(fifoTaxableProfit); // Losses first (harvest losses)
      } else if (hasTaxFreeGains) {
        priority = 5000 + fifoTaxableProfit; // Tax-free gains second
      } else {
        priority = 10000 + fifoTaxableProfit; // Regular taxable gains last
      }
      
      positionAnalysis.push({
        position,
        positionIndex,
        maxValue: position.totalValue,
        fifoTaxableProfit,
        taxPerEuro,
        hasLosses,
        hasTaxFreeGains,
        priority
      });
    });
    
    // Step 2: Sort positions by priority
    positionAnalysis.sort((a, b) => {
      if ((a.fifoTaxableProfit < 0) !== (b.fifoTaxableProfit < 0)) {
        return a.fifoTaxableProfit < 0 ? -1 : 1;
      }
      if (a.hasTaxFreeGains !== b.hasTaxFreeGains) {
        return a.hasTaxFreeGains ? -1 : 1;
      }
      return a.taxPerEuro - b.taxPerEuro;
    });
    
    // Step 3: Select positions until target is met
    const optimizedPositions = positions.map(pos => ({ ...pos, sellPercentage: 0 }));
    const sellInstructions: OptimizationSellInstruction[] = [];
    let remainingTarget = targetAmount;
    let totalProceeds = 0;

    for (const analysis of positionAnalysis) {
      if (remainingTarget <= 0) break;

      const saleValue = Math.min(analysis.maxValue, remainingTarget);
      const sellPercentage = (saleValue / analysis.maxValue) * 100;

      if (sellPercentage > 0) {
        const fifoTaxableProfit = this.calculateFIFOTaxableProfit(analysis.position, sellPercentage);
        const safeFifoTaxableProfit = typeof fifoTaxableProfit === 'number' ? fifoTaxableProfit : 0;

        optimizedPositions[analysis.positionIndex].sellPercentage = sellPercentage;

        const lotsToSell = this.calculateFIFOLotsToSell(analysis.position, sellPercentage);

        sellInstructions.push({
          position: analysis.position,
          positionIndex: analysis.positionIndex,
          positionKey: portfolioPositionKey(analysis.position),
          sellPercentage: sellPercentage,
          totalSaleValue: saleValue,
          totalTaxAmount: 0,
          totalTaxableProfit: safeFifoTaxableProfit,
          effectiveTaxableProfit: safeFifoTaxableProfit,
          usedSparerPauschbetrag: 0,
          taxTreatment: analysis.position.taxTreatment,
          positionName: analysis.position.name,
          priority: analysis.priority,
          lotsToSell: lotsToSell
        });

        totalProceeds += saleValue;
        remainingTarget -= saleValue;
      }
    }

    const rows = sellInstructions.map((instr) => ({
      positionKey: portfolioPositionKey(instr.position),
      gewinn: instr.totalTaxableProfit as number
    }));

    const net =
      rows.length > 0
        ? berechneNettoKapitalsteuerNachVerrechnung(
            rows,
            steuerParameter,
            sparerPauschbetragEnabled,
            sparerPauschbetragBetrag
          )
        : null;

    sellInstructions.forEach((instr) => {
      const key = portfolioPositionKey(instr.position);
      instr.totalTaxAmount = net?.gesamtsteuerProPosition.get(key) ?? 0;
      instr.usedSparerPauschbetrag = net?.verwendeterPauschbetragProPosition.get(key) ?? 0;
      instr.effectiveTaxableProfit = net?.steuerpflichtigerGewinnNachPauschProPosition.get(key) ?? 0;
    });

    const totalTaxAmount = net?.gesamtsteuer ?? 0;
    const totalFifoTaxableProfitBeforeAllowance = sellInstructions.reduce(
      (sum, instr) => sum + (instr.totalTaxableProfit || 0),
      0
    );
    const totalSteuerpflichtigerGewinnNachPausch = net?.steuerpflichtigerGewinnNachPausch ?? 0;
    const taxEfficiency = totalProceeds > 0 ? (totalProceeds - totalTaxAmount) / totalProceeds : 1;

    return {
      optimizedPositions,
      totalProceeds,
      totalTaxAmount,
      totalTaxableProfit: totalFifoTaxableProfitBeforeAllowance,
      totalFifoTaxableProfitBeforeAllowance,
      totalSteuerpflichtigerGewinnNachPausch,
      taxEfficiency,
      usedSparerPauschbetrag: net?.verwendeterPauschbetrag ?? 0,
      sellInstructions,
      strategy:
        'FIFO-konform, positionsübergreifende Gewinn-/Verlustverrechnung und Sparer-Pauschbetrag wie Tabellenansicht'
    };
  }
}